import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { LntButton, LntModal, LntForm, LntFormItem, LntDivider, LntSelect, LntModalFooter } from '@app/components/base'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { LOCATION_KEY, MONITORINGS_SUBTYPES_KEY, MONITORINGS_TYPES_KEY } from '@app/constants/queryKeys'

import { getStores } from '@app/api/routes/locationStores'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getClusterGroups } from '@app/api/routes/locationClusterGroups'
import { getSubtypesTypes, getTypes } from '@app/api/routes/monitorings'

import { isEmptyArray } from '@app/utils'
import { getFilterFromEntities } from '@app/utils/entitiesAdapters'

import styles from './styles.module.scss'

import type { LntSelectProps } from '@app/components/base/Select/types'
import type { CopyScheduleProps, CopyScheduleFromData } from './types'

const DEFAULT_TITLE = 'Копировать расписание'

const commonSelectProps: LntSelectProps = {
  showArrow: true,
  mode: 'multiple',
  size: SIZE.middle
}

export const CopySchedule: React.FC<CopyScheduleProps> = ({ onSubmit, title = DEFAULT_TITLE, ...restProps }) => {
  const formInstance = useForm<CopyScheduleFromData>()

  const hasMonitoringTypesIds = useMemo<boolean>(
    () => !isEmptyArray(formInstance.watch('monitoringTypeIds')),
    [formInstance.watch('monitoringTypeIds')]
  )

  const hasMonitoringSubtypeIds = useMemo<boolean>(
    () => !isEmptyArray(formInstance.watch('monitoringSubtypeIds')),
    [formInstance.watch('monitoringSubtypeIds')]
  )

  return (
    <LntModal
      { ...restProps }
      title={ title }
      footer={ null }
      width={ 444 }
    >
      <LntForm<CopyScheduleFromData>
        layout='vertical'
        formInstance={ formInstance }
        onSubmit={ onSubmit }
      >
        <LntFormItem
          name='monitoringTypeIds'
          label='Выберите тип мониторинга'
          defaultValue={ [] }
          className={ styles.copyScheduleModalFormItem }
        >
          <LntSelect
            { ...commonSelectProps }
            disabled={ hasMonitoringSubtypeIds }
            placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
            querySelect={ {
              queryKey: [MONITORINGS_TYPES_KEY.typesItems],
              queryFn: getTypes,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>

        <LntFormItem
          name='monitoringSubtypeIds'
          defaultValue={ [] }
          className={ styles.copyScheduleModalFormItem }
        >
          <LntSelect
            { ...commonSelectProps }
            disabled={ hasMonitoringTypesIds }
            placeholder={ FILTERS_PLACEHOLDERS.monitoringsSubtypesFilter }
            querySelect={ {
              queryKey: [MONITORINGS_SUBTYPES_KEY.subtypesItems],
              queryFn: getSubtypesTypes,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>

        <LntDivider />

        <LntFormItem
          name='storeIds'
          label='Выберите область применения'
          defaultValue={ [] }
          className={ styles.copyScheduleModalFormItem }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationStoresItems],
              queryFn: getStores,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>

        <LntFormItem
          name='clusterIds'
          defaultValue={ [] }
          className={ styles.copyScheduleModalFormItem }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationClustersItems],
              queryFn: getClusters,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>

        <LntFormItem
          name='clustersGroupIds'
          defaultValue={ [] }
          className={ styles.copyScheduleModalFormItem }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.clusterGroupsFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationClusterGroupsItems],
              queryFn: getClusterGroups,
              select: getFilterFromEntities
            } }
          />
        </LntFormItem>

        <LntModalFooter>
          <LntButton
            type={ BUTTON_TYPE.primary }
            size={ SIZE.middle }
            htmlType='submit'
          >
            Копировать
          </LntButton>
        </LntModalFooter>
      </LntForm>
    </LntModal>
  )
}
